import { useEffect, useState } from 'react';
import { FileUp, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  DEFAULT_PREBOARDING_DOCUMENT_FOLDER,
  type CreatePreboardingFormFieldInput,
  type PreboardingFormField,
  type PreboardingFormFieldKind,
  type MappableProfileField,
} from '@hygge/shared';
import { FolderSelect } from './folder-select';
import { MappingFieldSelect } from './mapping-field-select';

interface FieldEditorDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  field?: PreboardingFormField | null;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: CreatePreboardingFormFieldInput) => void;
}

const LABEL_MAX = 200;
const HELPER_MAX = 2000;

interface FormState {
  kind: PreboardingFormFieldKind;
  label: string;
  helperText: string;
  required: boolean;
  mappingKey: string | null;
  targetFolder: string;
}

function initialState(field: PreboardingFormField | null | undefined): FormState {
  return {
    kind: field?.kind ?? 'MAPPED',
    label: field?.label ?? '',
    helperText: field?.helperText ?? '',
    required: field?.required ?? false,
    mappingKey: field?.mappingKey ?? null,
    targetFolder: field?.targetFolder ?? DEFAULT_PREBOARDING_DOCUMENT_FOLDER,
  };
}

export function FieldEditorDialog({
  open,
  mode,
  field,
  isSubmitting = false,
  onOpenChange,
  onSubmit,
}: FieldEditorDialogProps) {
  const [form, setForm] = useState<FormState>(() => initialState(field));

  useEffect(() => {
    if (open) setForm(initialState(field));
  }, [open, field]);

  const trimmedLabel = form.label.trim();
  const addressingValid =
    form.kind === 'MAPPED' ? !!form.mappingKey : form.targetFolder.trim().length > 0;
  const canSubmit =
    !isSubmitting &&
    trimmedLabel.length > 0 &&
    trimmedLabel.length <= LABEL_MAX &&
    form.helperText.length <= HELPER_MAX &&
    addressingValid;

  // Picking a mapping target defaults the (still-empty) label to the field name.
  const handleMappingChange = (key: string, mapped: MappableProfileField) => {
    setForm((prev) => ({
      ...prev,
      mappingKey: key,
      label: prev.label.trim() ? prev.label : mapped.label,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      kind: form.kind,
      label: trimmedLabel,
      helperText: form.helperText.trim() || null,
      required: form.required,
      mappingKey: form.kind === 'MAPPED' ? form.mappingKey : null,
      targetFolder: form.kind === 'DOCUMENT' ? form.targetFolder.trim() : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{mode === 'create' ? 'Add field' : 'Edit field'}</DialogTitle>
            <DialogDescription>
              {form.kind === 'MAPPED'
                ? 'Collects a value that syncs to a profile field on the employee record.'
                : 'Requests a single file the candidate uploads to a documents folder.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Kind — immutable after creation */}
            {mode === 'create' ? (
              <div className="grid grid-cols-2 gap-2">
                <KindOption
                  active={form.kind === 'MAPPED'}
                  icon={<Link2 className="h-4 w-4" />}
                  title="Employee field"
                  caption="Maps to a profile attribute"
                  onClick={() => setForm((prev) => ({ ...prev, kind: 'MAPPED' }))}
                />
                <KindOption
                  active={form.kind === 'DOCUMENT'}
                  icon={<FileUp className="h-4 w-4" />}
                  title="Document upload"
                  caption="One file to a folder"
                  onClick={() => setForm((prev) => ({ ...prev, kind: 'DOCUMENT' }))}
                />
              </div>
            ) : null}

            {form.kind === 'MAPPED' ? (
              <div className="grid gap-2">
                <Label>Profile field</Label>
                <MappingFieldSelect value={form.mappingKey} onChange={handleMappingChange} />
                <p className="text-[11px] text-muted-foreground">
                  The submitted value is saved to this attribute on the employee profile.
                </p>
              </div>
            ) : (
              <div className="grid gap-2">
                <Label>Destination folder</Label>
                <FolderSelect
                  value={form.targetFolder}
                  onChange={(folder) => setForm((prev) => ({ ...prev, targetFolder: folder }))}
                />
                <p className="text-[11px] text-muted-foreground">
                  The uploaded file is routed to this folder in the employee documents.
                </p>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="field-label">Label</Label>
              <Input
                id="field-label"
                value={form.label}
                maxLength={LABEL_MAX}
                placeholder={form.kind === 'MAPPED' ? 'e.g. Tax ID' : 'e.g. Passport — page 1'}
                onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="field-helper">Helper text (optional)</Label>
              <Textarea
                id="field-helper"
                rows={2}
                value={form.helperText}
                maxLength={HELPER_MAX}
                placeholder="Instructions shown to the candidate under the field."
                onChange={(e) => setForm((prev) => ({ ...prev, helperText: e.target.value }))}
              />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
              <div>
                <Label htmlFor="field-required" className="text-sm">Required</Label>
                <p className="text-[11px] text-muted-foreground">
                  The candidate cannot submit without it.
                </p>
              </div>
              <Switch
                id="field-required"
                checked={form.required}
                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, required: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mode === 'create' ? 'Add field' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface KindOptionProps {
  active: boolean;
  icon: React.ReactNode;
  title: string;
  caption: string;
  onClick: () => void;
}

function KindOption({ active, icon, title, caption, onClick }: KindOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors',
        active
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-foreground/30 hover:bg-muted/40',
      )}
    >
      <span className={cn('flex items-center gap-1.5 text-sm font-medium', active && 'text-primary')}>
        {icon}
        {title}
      </span>
      <span className="text-[11px] text-muted-foreground">{caption}</span>
    </button>
  );
}
